import React from 'react';
import { Globe, Award, Users, Shield, GraduationCap, Plane, Heart } from 'lucide-react';

const About = () => {
  const services = [
    {
      icon: GraduationCap,
      title: "Student Visas",
      description: "Expert guidance for studying abroad"
    },
    {
      icon: Plane,
      title: "Work Permits",
      description: "Professional work visa assistance"
    },
    {
      icon: Globe,
      title: "PR Assistance",
      description: "Permanent residency solutions"
    },
    {
      icon: Users,
      title: "Visitor Visas",
      description: "Tourism and family visit support"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Transparency",
      description: "Clear processes and honest guidance at every step"
    },
    {
      icon: Award,
      title: "Success-Driven",
      description: "Proven track record of successful visa applications"
    },
    {
      icon: Heart,
      title: "End-to-End Support",
      description: "From profile evaluation to post-landing assistance"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              About Visaspot
            </h1>
            <p className="text-xl md:text-2xl text-white/90">
              Your Trusted Abroad Mobility Expert
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Who We Are */}
            <div className="mb-20">
              <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
                <div className="prose prose-lg max-w-none">
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                    Visaspot is a trusted <span className="font-semibold text-[#1565C0]">Abroad Mobility Expert</span>, dedicated to helping individuals achieve their global education and career dreams. We specialize in providing end-to-end visa solutions, including student visas, work permits, PR assistance, and visitor visas.
                  </p>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                    With a strong commitment to transparency and success, we guide our clients through every step — from profile evaluation and university selection to visa processing and post-landing support. Our additional services, such as scholarship assistance, education loans, and forex support, ensure a seamless journey abroad.
                  </p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-8 text-center py-6 px-4 bg-gradient-to-r from-[#FF7043]/10 to-[#8BC34A]/10 rounded-2xl">
                    At Visaspot, we don't just process visas — we build futures.
                  </p>
                </div>
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-20">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                Our Services
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <div 
                      key={index}
                      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center"
                    >
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1565C0] to-[#0D47A1] flex items-center justify-center mx-auto mb-4">
                        <Icon className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600">
                        {service.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Features */}
            <div className="mb-20">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                Why Choose Visaspot
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div 
                      key={index}
                      className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF7043]/20 to-[#8BC34A]/20 flex items-center justify-center mb-4">
                        <Icon className="text-[#1565C0]" size={28} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Additional Services */}
            <div className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] rounded-3xl p-8 md:p-12 text-white">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
                Comprehensive Support Services
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="text-5xl mb-3">🎓</div>
                  <h3 className="font-semibold text-lg mb-2">Scholarship Assistance</h3>
                  <p className="text-white/80 text-sm">Helping you secure funding</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl mb-3">💰</div>
                  <h3 className="font-semibold text-lg mb-2">Education Loans</h3>
                  <p className="text-white/80 text-sm">Financial support solutions</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl mb-3">💱</div>
                  <h3 className="font-semibold text-lg mb-2">Forex Support</h3>
                  <p className="text-white/80 text-sm">Currency exchange assistance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
